import React from "react";
import { InternalLinkProps } from "../sitemap/plp-sitemap";
import { SwarovskiLink } from "../../../components/_shared-components/ui-elements/SwarovskiLink";
import { css, SerializedStyles } from "@emotion/react";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { fontStyles } from "../../../styles/fonts";
import { NoImageSvg } from "../../../assets";
import { ContentfulImage2 } from "../../../components/_shared-components/image-related/ContentfulImage2";
import { CSS_MQ } from "../../../styles/breakpoints";
import { SimpleAspectRatio } from "../../../components/_shared-components/SimpleAspectRatio";

export const SparePartsCard: ReactTFC<{
  href: InternalLinkProps;
  image:
    | {
        url: string;
        sys: {
          id: string | undefined;
        };
      }
    | undefined;
  description: string | undefined;
  headline?: string;
  label?: React.ReactElement | string;
  expectSimilarLayout?: boolean;
  aspectRatioStyles: SerializedStyles;
}> = props => {
  const styles = css`
    width: 100%;
    height: 100%;
    object-fit: contain;
  `;

  const linkStyles = css`
    border: 1px solid ${CSS_COLORS.Grey4};
    margin: 1px;
    border-radius: 8px;

    &:hover {
      margin: 0;
      border: 2px solid ${CSS_COLORS.Green};
      box-shadow: 0 0 10px 0 rgba(31, 112, 87, 0.5);
    }
  `;

  const element = props.image ? (
    <ContentfulImage2
      assetId={props.image.sys.id}
      contentfulUrl={props.image.url}
      withTransparency={true}
      loading={"lazy"}
      alt={props.description}
      css={styles}
      sizes="(max-width: 700px) 100vw, 600px"
      cropping={undefined}
    />
  ) : (
    <div
      css={[
        styles,
        css`
          display: flex;
          justify-content: center;
          align-items: center;
        `,
      ]}
    >
      <NoImageSvg
        css={css`
          color: ${CSS_COLORS.Grey2};

          width: 42px;
          ${CSS_MQ.atLeastMd} {
            width: 60px;
          }
        `}
      />
    </div>
  );

  return (
    <SwarovskiLink
      isInternal={true}
      expectSimilarLayout={!!props.expectSimilarLayout}
      scrollToTop={true}
      link={props.href}
      styled={"no-decoration"}
      attachStyles={linkStyles}
    >
      <div
        css={css`
          padding: 20px 12px;
          ${CSS_MQ.atLeastLg} {
            padding: 30px;
          }
        `}
      >
        <SimpleAspectRatio css={props.aspectRatioStyles}>{element}</SimpleAspectRatio>
      </div>

      <div
        css={css`
          padding-left: 12px;
          padding-right: 12px;
          padding-bottom: 10px;

          ${CSS_MQ.atLeastLg} {
            padding-left: 30px;
            padding-right: 30px;
            padding-bottom: 25px;
          }
        `}
      >
        {props.headline && (
          <span
            css={[
              fontStyles.headline2,
              css`
                display: block;

                margin-bottom: 4px;
                ${CSS_MQ.atLeastLg} {
                  margin-bottom: 8px;
                }
              `,
            ]}
          >
            {props.headline}
          </span>
        )}

        {props.description && (
          <span
            css={[
              fontStyles.headline4,
              css`
                display: block;
                margin-bottom: 4px;
                ${CSS_MQ.atLeastLg} {
                  margin-bottom: 8px;
                }
              `,
            ]}
          >
            {props.description}
          </span>
        )}

        <span css={[fontStyles.headline3]}>{props.label}</span>
      </div>
    </SwarovskiLink>
  );
};

export const aspectRatioStyles = css`
  --aspect-ratio: ${2 / 3};

  ${CSS_MQ.atLeastLg} {
    --aspect-ratio: ${2 / 3};
  }
`;
